import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Crown, Gem, Building2, Check, Loader2, ArrowLeft, Infinity as InfinityIcon, Clock } from 'lucide-react';
import PortalLayout from './portal/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const baseFeatures = (p) => [
  `${p.posts_per_day} posts / day`,
  `${p.videos_per_day} video${p.videos_per_day === 1 ? '' : 's'} / day`,
  `${p.max_brand_profiles == null ? 'Unlimited' : p.max_brand_profiles} brand profile${p.max_brand_profiles === 1 ? '' : 's'}`,
  `${p.max_library_uploads == null ? 'Unlimited' : p.max_library_uploads} library uploads`,
  `Content plans up to ${p.max_content_plan_days} days`,
];

const TIER_UI = {
  starter: { Icon: Sparkles, color: '#1D4ED8', tagline: 'Best for launching your brand' },
  pro: { Icon: Crown, color: '#7C3AED', tagline: 'Best for active business owners' },
  premium: { Icon: Gem, color: '#0EA5E9', tagline: 'Best for power users and growing teams' },
  agency: { Icon: Building2, color: '#0F766E', tagline: 'For coaches, agencies and multi-brand creators' },
};

export default function StudioPlans() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState(null);

  useEffect(() => {
    api.get('/api/admin/social-studio/usage').then((r) => setUsage(r.data)).catch(() => setUsage(null)).finally(() => setLoading(false));
  }, []);

  const changePlan = async (tier, isDowngrade) => {
    if (isDowngrade && !window.confirm(`Switch down to the ${TIER_UI[tier] ? plans[tier]?.plan_name : tier} plan? Your billing will be prorated and your limits will change.`)) return;
    setBusyTier(tier);
    try {
      const r = await api.post('/api/social-studio/subscribe/checkout', { tier, origin_url: window.location.origin });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      if (r.data?.changed) {
        toast.success(`You're now on the ${r.data.plan_name || tier} plan.`);
        const u = await api.get('/api/admin/social-studio/usage').then((x) => x.data).catch(() => null);
        if (u) setUsage(u);
      } else {
        toast.error('Could not change plan. Please try again.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Plan change failed. Please try again.');
    } finally {
      setBusyTier(null);
    }
  };

  const tier = usage?.tier;
  const isAdmin = tier === 'admin';
  const rank = { starter: 1, pro: 2, premium: 3, agency: 4 };
  const plans = usage?.plans || {};
  // Show starter + pro + premium always; agency only when public.
  const visibleTiers = ['starter', 'pro', 'premium', 'agency'].filter((t) => plans[t] && (t !== 'agency' || plans[t].is_public));

  return (
    <PortalLayout active="studio">
      <div className="mb-6" data-testid="studio-plans-page">
        <Link to="/admin/social-studio" className="flex w-fit items-center gap-1.5 text-sm text-[#7C3AED] hover:underline mb-4" data-testid="studio-plans-back">
          <ArrowLeft size={15} /> Back to Studio
        </Link>
        <span className="block text-xs tracking-[0.2em] uppercase text-[#7C3AED] font-semibold">Studio Plans</span>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Choose your generation power
        </h1>
        <p className="mt-2 max-w-2xl" style={{ color: 'var(--p-muted)' }}>
          Every plan unlocks the full Studio. Higher tiers raise your daily limits, plan length, brands and uploads. Daily limits reset every morning.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#7C3AED]" size={28} /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl">
          {visibleTiers.map((t) => {
            const p = plans[t];
            const ui = TIER_UI[t];
            const { Icon } = ui;
            const isCurrent = tier === t;
            const isHigher = !isAdmin && rank[t] > (rank[tier] || 0);
            const features = baseFeatures(p);
            if (t === 'pro' || t === 'premium') features.push('Scheduler & auto-publishing coming soon');
            return (
              <div key={t} className={`relative overflow-hidden rounded-2xl p-6 flex flex-col ${isCurrent ? 'border-2 pt-12' : 'border'}`}
                style={{ borderColor: isCurrent ? ui.color : ui.color + '40', boxShadow: isCurrent ? `0 14px 38px -18px ${ui.color}, 0 0 0 1px ${ui.color}` : 'none', background: isCurrent ? `color-mix(in srgb, ${ui.color} 7%, var(--p-surface))` : 'var(--p-surface)' }}
                data-testid={`plan-card-${t}`}>
                {isCurrent && (
                  <div className="absolute top-0 left-0 right-0 text-center text-white text-[11px] font-black uppercase tracking-[0.18em] py-1.5"
                    style={{ background: ui.color }} data-testid={`plan-${t}-current-banner`}>
                    Your current plan
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-xs font-black" style={{ background: ui.color }}>
                    <Icon size={13} /> {p.plan_name}
                  </span>
                  {isCurrent && <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider" style={{ color: ui.color }}><Check size={13} /> Current</span>}
                </div>
                <p className="mt-4 text-3xl font-light" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ${Number(p.monthly_price).toFixed(0)}<span className="text-base" style={{ color: 'var(--p-faint)' }}>/mo</span>
                </p>
                <p className="text-sm mb-3" style={{ color: 'var(--p-faint)' }}>{ui.tagline}</p>
                <ul className="space-y-2 text-sm flex-1" style={{ color: 'var(--p-muted)' }}>
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check size={15} className="text-emerald-500 mt-0.5 flex-shrink-0" /> {f}</li>
                  ))}
                </ul>
                <div className="mt-6">
                  {isAdmin ? (
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--p-text)]"><InfinityIcon size={16} /> Unlimited admin access</span>
                  ) : isCurrent ? (
                    <button disabled className="w-full font-bold py-3 rounded-lg cursor-default" style={{ background: ui.color + '22', color: ui.color }} data-testid={`plan-${t}-current`}>Your current plan</button>
                  ) : isHigher ? (
                    <button onClick={() => changePlan(t, false)} disabled={busyTier === t} data-testid={`plan-${t}-subscribe`}
                      className="w-full inline-flex items-center justify-center gap-2 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-60"
                      style={{ background: ui.color }}>
                      {busyTier === t ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />} Upgrade to {p.plan_name}
                    </button>
                  ) : (
                    <button onClick={() => changePlan(t, true)} disabled={busyTier === t} data-testid={`plan-${t}-downgrade`}
                      className="w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-lg border transition-colors disabled:opacity-60"
                      style={{ borderColor: ui.color, color: ui.color }}>
                      {busyTier === t ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />} Downgrade to {p.plan_name}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PortalLayout>
  );
}
